import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';

const TermsOfUse = () => {
  const { user, userProfile, isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: [
        {
          text: 'By accessing and using BardPlay ("the Platform"), you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services. These terms constitute a legally binding agreement between you and BardPlay.'
        },
        {
          text: 'Your continued use of the Platform following any changes to these terms constitutes acceptance of those changes. We reserve the right to modify these terms at any time, and will notify users of significant changes via email or platform notification.'
        }
      ]
    },
    {
      id: 'account-creation',
      title: '2. Account Creation and Responsibilities',
      content: [
        {
          subtitle: 'Eligibility',
          text: 'You must be at least 13 years old to create an account. Users under 13 may use the Platform only with parental consent or through a school-managed account. Users under 18 should have parental or guardian permission.'
        },
        {
          subtitle: 'Account Security',
          text: 'You are responsible for maintaining the confidentiality of your account credentials. You must immediately notify us of any unauthorized use of your account. You agree to provide accurate, current, and complete information during registration and to update this information as needed.'
        },
        {
          subtitle: 'Account Types',
          text: 'Different account types (Student, Educator, Administrator) have different access levels and responsibilities. Educators and administrators have additional responsibilities regarding student data and privacy.'
        }
      ]
    },
    {
      id: 'acceptable-use',
      title: '3. Acceptable Use Policy',
      content: [
        {
          subtitle: 'Permitted Uses',
          text: 'BardPlay is designed for educational purposes related to Shakespeare study. You may use the Platform to read plays, complete quizzes, track progress, collaborate with classmates, and access educational resources.'
        },
        {
          subtitle: 'Prohibited Activities',
          text: 'You agree NOT to: (a) violate any laws or regulations; (b) infringe on intellectual property rights; (c) transmit harmful code or malware; (d) harass, abuse, or harm other users; (e) attempt to gain unauthorized access to the Platform; (f) use automated systems to access the Platform without permission; (g) share account credentials; (h) use the Platform for commercial purposes without authorization.'
        },
        {
          subtitle: 'Educational Use Requirements',
          text: 'Students must complete their own work and not engage in academic dishonesty. Educators must use the Platform in accordance with their institution\'s policies and maintain appropriate professional boundaries with students.'
        }
      ]
    },
    {
      id: 'intellectual-property',
      title: '4. Intellectual Property Rights',
      content: [
        {
          subtitle: 'Platform Content',
          text: 'All content on BardPlay, including text, graphics, logos, software, and educational materials, is owned by or licensed to BardPlay and protected by copyright, trademark, and other intellectual property laws.'
        },
        {
          subtitle: 'Shakespeare Content',
          text: 'Shakespeare\'s works are in the public domain. Our annotations, glossaries, quizzes, and educational enhancements are proprietary and protected by copyright. You may not reproduce, distribute, or create derivative works without permission.'
        },
        {
          subtitle: 'User-Generated Content',
          text: 'You retain ownership of content you create on the Platform. By submitting content, you grant BardPlay a worldwide, non-exclusive license to use, reproduce, and display your content for educational purposes within the Platform.'
        },
        {
          subtitle: 'Attribution',
          text: 'When using BardPlay materials for educational purposes, proper attribution must be provided. Commercial use of Platform content requires written permission.'
        }
      ]
    },
    {
      id: 'service-availability',
      title: '5. Service Availability and Modifications',
      content: [
        {
          subtitle: 'Platform Availability',
          text: 'We strive to maintain continuous Platform availability but cannot guarantee uninterrupted access. We may perform maintenance, updates, or modifications that temporarily limit access. We will provide advance notice of scheduled maintenance when possible.'
        },
        {
          subtitle: 'Feature Changes',
          text: 'We reserve the right to modify, suspend, or discontinue any feature or aspect of the Platform at any time. We may add, change, or remove features to improve educational outcomes or address technical requirements.'
        },
        {
          subtitle: 'User Notification',
          text: 'Significant changes to core educational features will be communicated to users with reasonable advance notice. Users will be notified via email or platform notification.'
        }
      ]
    },
    {
      id: 'termination',
      title: '6. Account Termination',
      content: [
        {
          subtitle: 'Voluntary Termination',
          text: 'You may terminate your account at any time through account settings. Upon termination, your access to the Platform will cease, and your data will be handled according to our Privacy Policy.'
        },
        {
          subtitle: 'Termination by BardPlay',
          text: 'We reserve the right to suspend or terminate accounts that violate these Terms of Use, engage in prohibited activities, or pose security risks. We will provide notice when possible, except in cases of severe violations or legal requirements.'
        },
        {
          subtitle: 'Effects of Termination',
          text: 'Upon termination, you lose access to Platform features, your progress data, and any premium content. Some data may be retained for legal compliance or legitimate business purposes as described in our Privacy Policy.'
        }
      ]
    },
    {
      id: 'limitation-liability',
      title: '7. Limitation of Liability',
      content: [
        {
          subtitle: 'Disclaimer of Warranties',
          text: 'The Platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied. We do not warrant that the Platform will be error-free, secure, or continuously available.'
        },
        {
          subtitle: 'Educational Content Disclaimer',
          text: 'While we strive for accuracy in our educational content, we do not guarantee the completeness or accuracy of all materials. Educational interpretations and analyses represent informed perspectives but are not definitive academic sources.'
        },
        {
          subtitle: 'Limitation of Damages',
          text: 'To the maximum extent permitted by law, BardPlay shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, lost data, or educational setbacks, arising from your use of the Platform.'
        },
        {
          subtitle: 'Maximum Liability',
          text: 'Our total liability for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid to BardPlay in the 12 months preceding the claim.'
        }
      ]
    },
    {
      id: 'dispute-resolution',
      title: '8. Dispute Resolution',
      content: [
        {
          subtitle: 'Informal Resolution',
          text: 'If you have a dispute with BardPlay, please contact us first at support@bardplay.com to attempt informal resolution. We commit to working in good faith to resolve disputes.'
        },
        {
          subtitle: 'Binding Arbitration',
          text: 'Any dispute that cannot be resolved informally shall be resolved through binding arbitration rather than court proceedings. You and BardPlay agree to arbitrate all disputes except those relating to intellectual property rights or small claims court matters.'
        },
        {
          subtitle: 'Class Action Waiver',
          text: 'You agree to bring claims only in your individual capacity and not as part of a class action or representative proceeding.'
        }
      ]
    },
    {
      id: 'governing-law',
      title: '9. Governing Law and Jurisdiction',
      content: [
        {
          text: 'These Terms of Use are governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to conflict of law principles. Any legal action or proceeding arising under these terms will be brought exclusively in courts located in Delaware.'
        }
      ]
    },
    {
      id: 'miscellaneous',
      title: '10. Miscellaneous Provisions',
      content: [
        {
          subtitle: 'Entire Agreement',
          text: 'These Terms of Use, together with our Privacy Policy, constitute the entire agreement between you and BardPlay regarding use of the Platform.'
        },
        {
          subtitle: 'Severability',
          text: 'If any provision of these terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.'
        },
        {
          subtitle: 'No Waiver',
          text: 'Our failure to enforce any provision of these terms shall not constitute a waiver of that provision or any other provision.'
        },
        {
          subtitle: 'Assignment',
          text: 'You may not assign or transfer these terms or your account without our written consent. BardPlay may assign these terms in connection with a merger, acquisition, or sale of assets.'
        },
        {
          subtitle: 'Contact Information',
          text: 'For questions about these Terms of Use, please contact us at legal@bardplay.com or through our contact page.'
        }
      ]
    },
    {
      id: 'updates',
      title: '11. Updates and Amendments',
      content: [
        {
          subtitle: 'Effective Date',
          text: 'These Terms of Use are effective as of December 12, 2025. Last updated: December 12, 2025.'
        },
        {
          subtitle: 'Notification of Changes',
          text: 'Material changes to these terms will be communicated via email to your registered address and/or prominent notice on the Platform at least 30 days before taking effect. Continued use after changes become effective constitutes acceptance.'
        },
        {
          subtitle: 'Version History',
          text: 'Previous versions of these terms are available upon request for users who need to review historical agreements.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  const filteredSections = sections?.filter(section =>
    searchTerm === '' || 
    section?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    section?.content?.some(item => 
      item?.text?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.subtitle?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
  );

  return (
    <>
      <Helmet>
        <title>Terms of Use - BardPlay</title>
        <meta name="description" content="Read BardPlay's Terms of Use covering acceptable use policies, account responsibilities, intellectual property rights, and service agreements." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Header */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
                <Icon name="FileText" size={20} className="text-primary" />
                <span className="text-primary font-semibold">Legal Agreement</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Terms of Use</h1>
              <p className="text-lg text-white/70">
                Last Updated: December 12, 2025
              </p>
              <p className="text-white/60 mt-4 max-w-2xl mx-auto">
                Please read these Terms of Use carefully before using BardPlay. By accessing or using our Platform, 
                you agree to be bound by these terms and all applicable laws and regulations.
              </p>
            </div>

            {/* Search Bar */}
            <div className="mt-8 max-w-2xl mx-auto">
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text"
                  placeholder="Search terms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-12 pr-4 py-3 bg-surface-dark/50 backdrop-blur-sm border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Terms Content */}
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="bg-surface-dark/50 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
              {filteredSections?.length > 0 ? (
                filteredSections?.map((section, index) => (
                  <div key={section?.id} className={`border-b border-white/10 ${index === filteredSections?.length - 1 ? 'border-b-0' : ''}`}>
                    <button
                      onClick={() => toggleSection(section?.id)}
                      className="w-full px-6 py-5 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                    >
                      <h2 className="text-xl font-bold text-white text-left">{section?.title}</h2>
                      <Icon 
                        name={activeSection === section?.id ? 'ChevronUp' : 'ChevronDown'} 
                        size={24} 
                        className="text-white/60 flex-shrink-0"
                      />
                    </button>
                    
                    {activeSection === section?.id && (
                      <div className="px-6 pb-6 space-y-6">
                        {section?.content?.map((item, idx) => (
                          <div key={idx}>
                            {item?.subtitle && (
                              <h3 className="text-lg font-semibold text-white mb-2">{item?.subtitle}</h3>
                            )}
                            <p className="text-white/70 leading-relaxed">{item?.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="p-12 text-center">
                  <Icon name="Search" size={48} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No matching sections found for "{searchTerm}"</p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-4 text-primary hover:text-primary-dark transition-colors"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>

            {/* Important Notice */}
            <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Important Notice</h3>
                  <p className="text-white/70 mb-4">
                    By using BardPlay, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use. 
                    If you do not agree to these terms, please discontinue use of the Platform immediately.
                  </p>
                  <p className="text-white/60 text-sm">
                    For questions or concerns about these terms, please contact our legal team at legal@bardplay.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default TermsOfUse;